import {
  AUTH_FORCE_LOGOUT_HEADER,
  AUTH_FORCE_LOGOUT_REASON_HEADER,
  buildForceLogoutSignInDestination,
} from './sessionIntegrity';

declare global {
  interface Window {
    __mindLabsForceLogoutRedirectInProgress?: boolean;
  }
}

export const getCurrentBrowserPath = (): string => {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const triggerClientForceLogout = (reason?: string) => {
  if (typeof window === 'undefined') return;
  if (window.__mindLabsForceLogoutRedirectInProgress) return;

  window.__mindLabsForceLogoutRedirectInProgress = true;
  const destination = buildForceLogoutSignInDestination({
    callbackUrl: getCurrentBrowserPath(),
    reason,
  });
  window.location.assign(destination);
};

export const getForceLogoutReasonFromHeaders = (
  headers:
    | Headers
    | Record<string, string | string[] | undefined>
    | { get?: (name: string) => string | null | undefined }
    | undefined
): string | null => {
  if (!headers) return null;

  const normalizeHeaderValue = (
    value: string | string[] | null | undefined
  ): string | null => {
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }
    return value ?? null;
  };

  const readHeader = (name: string): string | null => {
    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      return headers.get(name);
    }

    const getterOwner = headers as {
      get?: (key: string) => string | null | undefined;
    };
    const getter = getterOwner.get;
    if (typeof getter === 'function') {
      const readWithOwner = (key: string) => getter.call(getterOwner, key);
      return readWithOwner(name) ?? readWithOwner(name.toLowerCase()) ?? null;
    }

    const record = headers as Record<string, string | string[] | undefined>;
    return normalizeHeaderValue(record[name] ?? record[name.toLowerCase()]);
  };

  if (readHeader(AUTH_FORCE_LOGOUT_HEADER) !== '1') return null;
  return readHeader(AUTH_FORCE_LOGOUT_REASON_HEADER);
};

export const isSessionIdentityMismatchMessage = (
  message?: string | null
): message is string =>
  typeof message === 'string' &&
  /userId in request does not match the current session\/token/i.test(message);
